import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Dialog, IconButton, Tooltip } from '@material-ui/core';
import { Close, EmojiEvents, OpenInNew, Schedule, SportsEsports, Block, Timer, CheckCircle, HourglassEmpty } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { AocClasses, ICharacter } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { IUser } from '@pvplevelup/core/dist/interfaces/IUser';
import { GameModes } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { ITournament, GamePhase, MatchStatus, TournamentType } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { IMatchView, IGameView, IDraftLogView, ITeamPickView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { getFullMapPool, GROUP_MODES } from '@pvplevelup/core/dist/tournament/TournamentRules';
import { matchLabel } from '@pvplevelup/core/dist/tournament/TournamentSchedule';
import { winsNeeded } from '@pvplevelup/core/dist/tournament/TournamentDraft';
import { classImages } from 'utilities/ClassImages';
import { PAGE_KIT } from 'components/Page/PageKit';
import { TOURNAMENT_ACCENT, formatDateTime, formatCountdown } from './tournamentFormat';

const SIDE_COLORS = {a: '#42a5f5', b: '#ef6c00'};
const BAN = '#ef5350';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  paper: {
    width: 920,
    maxWidth: 'calc(100vw - 32px)',
    height: 'min(780px, calc(100vh - 48px))',
    borderRadius: 18,
    overflow: 'hidden',
    background: '#121317',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 30px 90px rgba(0, 0, 0, 0.7)',
  },
  shell: {
    display: 'grid',
    gridTemplateRows: 'auto auto minmax(0, 1fr)',
    height: '100%',
    minHeight: 0,
  },
  head: {
    position: 'relative',
    padding: theme.spacing(2, 2.5, 1.75),
    background: 'radial-gradient(circle at 50% -60%, rgba(255, 179, 0, 0.2), transparent 65%), #16171c',
  },
  close: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  kicker: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'center',
    gridColumnGap: 8,
    alignItems: 'center',
    fontSize: '0.7rem',
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: TOURNAMENT_ACCENT,
  },
  status: {
    padding: '1px 8px',
    borderRadius: 8,
    fontSize: '0.64rem',
    letterSpacing: '0.06em',
  },
  versus: {
    marginTop: theme.spacing(1.25),
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    gridColumnGap: 18,
    alignItems: 'center',
  },
  sideName: {
    fontSize: '1.2rem',
    fontWeight: 900,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sideSub: {
    fontSize: '0.74rem',
    fontWeight: 700,
    color: PAGE_KIT.faint,
  },
  winnerName: {
    color: TOURNAMENT_ACCENT,
  },
  score: {
    textAlign: 'center',
    fontSize: '2rem',
    fontWeight: 900,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  scoreSub: {
    marginTop: 4,
    fontSize: '0.72rem',
    fontWeight: 700,
    color: PAGE_KIT.muted,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  tabs: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridColumnGap: 4,
    padding: theme.spacing(0, 2),
    background: '#16171c',
    borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
  },
  tab: {
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: '0.84rem',
    fontWeight: 800,
    color: PAGE_KIT.muted,
    borderBottom: '2px solid transparent',
    userSelect: 'none',
    '&:hover': {
      color: '#fff',
    },
  },
  tabOn: {
    color: '#fff',
    borderBottomColor: TOURNAMENT_ACCENT,
  },
  body: {
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.25, 2.5, 3),
  },
  // ---------------------------------------------------------------- overview
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gridGap: 10,
  },
  fact: {
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.07)',
  },
  factLabel: {
    fontSize: '0.64rem',
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
  },
  factValue: {
    marginTop: 3,
    fontWeight: 800,
  },
  section: {
    marginTop: theme.spacing(2.5),
  },
  label: {
    marginBottom: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
  },
  rosters: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 12,
  },
  roster: {
    borderRadius: 14,
    padding: theme.spacing(1.25, 1.5),
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.07)',
  },
  rosterTitle: {
    marginBottom: 8,
    fontWeight: 900,
  },
  player: {
    display: 'grid',
    gridTemplateColumns: '18px minmax(0, 1fr) auto',
    gridColumnGap: 8,
    alignItems: 'center',
    minHeight: 34,
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  },
  playerName: {
    fontWeight: 700,
    fontSize: '0.88rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    '&:hover': {
      color: TOURNAMENT_ACCENT,
    },
  },
  icons: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridColumnGap: 3,
  },
  icon: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'block',
  },
  muted: {
    fontSize: '0.84rem',
    color: PAGE_KIT.faint,
  },
  // ---------------------------------------------------------------- games
  game: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  gameLive: {
    borderColor: 'rgba(239, 83, 80, 0.55)',
    boxShadow: '0 0 0 1px rgba(239, 83, 80, 0.2)',
  },
  gameHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridColumnGap: 12,
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.75),
    background: 'rgba(0, 0, 0, 0.2)',
  },
  gameTitle: {
    fontWeight: 900,
  },
  gameMeta: {
    marginTop: 2,
    fontSize: '0.78rem',
    color: PAGE_KIT.muted,
  },
  gameBody: {
    padding: theme.spacing(1.5, 1.75),
  },
  gameSides: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 12,
  },
  pickRow: {
    display: 'grid',
    gridTemplateColumns: '26px minmax(0, 1fr)',
    gridColumnGap: 8,
    alignItems: 'center',
    minHeight: 30,
    fontSize: '0.84rem',
  },
  pickIcon: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'block',
  },
  banIcon: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'block',
    filter: 'grayscale(1)',
    boxShadow: `0 0 0 2px ${BAN}`,
  },
  timeline: {
    display: 'grid',
    gridRowGap: 6,
  },
  step: {
    display: 'grid',
    gridTemplateColumns: '24px 64px 26px minmax(0, 1fr) auto',
    gridColumnGap: 8,
    alignItems: 'center',
    padding: '5px 8px',
    borderRadius: 10,
    fontSize: '0.84rem',
    background: 'rgba(255, 255, 255, 0.025)',
  },
  stepNo: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: '0.7rem',
    fontWeight: 900,
    background: 'rgba(255, 255, 255, 0.08)',
  },
  stepAction: {
    fontSize: '0.66rem',
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  tag: {
    padding: '1px 7px',
    borderRadius: 7,
    fontSize: '0.64rem',
    fontWeight: 900,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  openGame: {
    height: 32,
    textTransform: 'none',
    fontWeight: 800,
  },
}));

const idOf = (x: any) => String(x?._id || x || '');
const icon = (c?: ICharacter | null) => c ? classImages.getColorIcon(c.class as AocClasses) : '';
const MODE_PATHS: Record<string, string> = {
  [GameModes.TeamDeathMatch]: 'tdm', [GameModes.KingoftheHill]: 'koh', [GameModes.Conquest]: 'conquest', [GameModes.CaptureTheFlag]: 'capturetheflag',
};

const STATUS: Record<string, {label: string, color: string}> = {
  [MatchStatus.Pending]: {label: 'Waiting for its teams', color: '#90a4ae'},
  [MatchStatus.Ready]: {label: 'Ready', color: '#ffb300'},
  [MatchStatus.Live]: {label: 'Live', color: '#ef5350'},
  [MatchStatus.Completed]: {label: 'Final', color: '#66bb6a'},
  [MatchStatus.Bye]: {label: 'Bye', color: '#90a4ae'},
  [MatchStatus.Void]: {label: 'Not needed', color: '#78909c'},
};

const PHASES: Record<string, string> = {
  [GamePhase.CheckIn]: 'Checking in', [GamePhase.Choose]: 'Choosing the game', [GamePhase.Draft]: 'Drafting', [GamePhase.Playing]: 'Being played',
};

interface Props {
  tournament: ITournament;
  match: IMatchView | null;
  /** every match (to name the round: "Semifinals · match 1") */
  matches: IMatchView[];
  onClose: () => void;
  /** organizers and admins (the app): the Manage tab's content */
  manage?: React.ReactNode;
  /** where a player's profile lives (the app: /users, the website: /user) */
  userPath?: string;
}

type Tab = 'overview' | 'games' | 'manage';

/**
 * A matchup in detail, opened from the bracket: how it stands, both sides' players and characters, every game with its
 * picks, bans and draft order (and a link to open the game), and for organizers the management tools. Shared by the app
 * and the website.
 */
export const MatchDetail: React.FC<Props> = ({tournament: t, match: m, matches, onClose, manage, userPath = '/users'}) => {
  const classes = useStyles();
  const history = useHistory();
  const [tab, setTab] = useState<Tab>('overview');
  const [now, setNow] = useState(Date.now());
  useEffect(() => { setTab('overview'); }, [m?._id]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const mapInfo = useMemo(() => {
    const byId: Record<number, {name: string, zoneName: string}> = {};
    getFullMapPool(t.server, GROUP_MODES, true).forEach((x) => byId[x.id] = x);
    return byId;
  }, [t.server]);
  if(!m)
    return null;

  const totals: Record<string, number> = {};
  matches.forEach((x) => totals[x.stage] = Math.max(totals[x.stage] || 0, x.round));
  const title = matchLabel(m as any, totals);
  const status = STATUS[m.status] || STATUS.Pending;
  const team = t.type == TournamentType.GroupPvP;
  const mapName = (id?: number | null) => id != null ? (mapInfo[id]?.name || `Map ${id}`) : null;
  const sideName = (s: 'a' | 'b') => m[s].name || m[s].fromLabel || (m[s].bye ? 'Bye' : 'To be decided');
  const games = m.games || [];
  const current = m.current && m.status == MatchStatus.Live && m.current.phase != GamePhase.CheckIn ? m.current : null;
  const gameCount = games.length + (current && !games.some((g) => g.index == current.index) ? 1 : 0);
  const openProfile = (user?: IUser | null) => user?._id && history.push(`${userPath}/${user._id}`);

  // ---------------------------------------------------------------- where it stands
  let state = status.label;
  if(m.status == MatchStatus.Live && m.current)
    state = `Game ${m.current.index} · ${PHASES[m.current.phase] || m.current.phase}`;
  else if(m.status == MatchStatus.Ready && !m.readySince && m.startsAt)
    state = `Starts ${formatCountdown(m.startsAt, now)}`;
  else if(m.status == MatchStatus.Ready && m.current?.phase == GamePhase.CheckIn)
    state = 'Checking in';

  const overview = (
    <React.Fragment>
      <div className={classes.facts}>
        <div className={classes.fact}><div className={classes.factLabel}>Status</div><div className={classes.factValue} style={{color: status.color}}>{state}</div></div>
        <div className={classes.fact}><div className={classes.factLabel}>Series</div><div className={classes.factValue}>Best of {m.bestOf} · first to {winsNeeded(m.bestOf)}</div></div>
        {(m.startsAt || m.readySince) &&
          <div className={classes.fact}>
            <div className={classes.factLabel}>{m.readySince ? 'Opened' : m.scheduledAt ? 'Starts (its own time)' : 'Starts (round time)'}</div>
            <div className={classes.factValue}>{formatDateTime(m.readySince || m.startsAt)}</div>
          </div>}
        {m.status == MatchStatus.Ready && m.readySince && m.deadline &&
          <div className={classes.fact}><div className={classes.factLabel}>Check-in closes</div><div className={classes.factValue}>{formatCountdown(m.deadline, now)}</div></div>}
        {team && m.firstMode && <div className={classes.fact}><div className={classes.factLabel}>Game 1</div><div className={classes.factValue}>{m.firstMode}{m.firstMap != null ? ` · ${mapName(m.firstMap)}` : ''}</div></div>}
      </div>

      {team && (m.mapPool || []).length > 0 &&
        <div className={classes.section}>
          <div className={classes.label}>Map pool</div>
          <div className={classes.muted} style={{lineHeight: 1.7}}>
            {(m.mapPool || []).map((id) => mapName(id)).join(' · ')}
          </div>
        </div>}

      <div className={classes.section}>
        <div className={classes.label}>{team ? 'Teams' : 'Players'}</div>
        <div className={classes.rosters}>
          {(['a', 'b'] as Array<'a' | 'b'>).map((s) => {
            const checked = (s == 'a' ? m.checkInUsersA : m.checkInUsersB) || [];
            const checkingIn = m.status == MatchStatus.Ready && !!m.readySince;
            const players = m[s].players || [];
            return (
              <div key={s} className={classes.roster} style={{borderColor: m.winner == s ? 'rgba(255, 179, 0, 0.5)' : undefined}}>
                <div className={classes.rosterTitle} style={{color: SIDE_COLORS[s]}}>
                  {m.winner == s && <EmojiEvents style={{fontSize: 16, color: TOURNAMENT_ACCENT, verticalAlign: -3, marginRight: 4}} />}
                  {sideName(s)}{m[s].seed ? <span className={classes.sideSub}> · seed {m[s].seed}</span> : null}
                </div>
                {players.length == 0 && <div className={classes.muted}>{m[s].fromLabel ? `Decided by ${String(m[s].fromLabel).replace(/^Winner of |^Loser of /, '')}` : 'Not known yet.'}</div>}
                {players.map((p) => {
                  const done = checked.indexOf(idOf(p.user)) != -1;
                  return (
                    <div key={idOf(p.user)} className={classes.player}>
                      {checkingIn ? (done ? <CheckCircle style={{fontSize: 15, color: '#81c784'}} /> : <HourglassEmpty style={{fontSize: 14, opacity: 0.5}} />) : <span />}
                      <span className={classes.playerName} onClick={() => openProfile(p.user as IUser)}>{(p.user as IUser)?.displayname}</span>
                      <span className={classes.icons}>
                        {(p.characters || []).map((c) => (
                          <Tooltip key={idOf(c)} title={`${c.name} · ${c.class}`}><img className={classes.icon} src={icon(c)} alt={c.class} /></Tooltip>
                        ))}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );

  // ---------------------------------------------------------------- the games
  const picksOf = (g: {teamPicksA?: ITeamPickView[], teamPicksB?: ITeamPickView[], pickA?: ICharacter | null, pickB?: ICharacter | null}, s: 'a' | 'b'): Array<{user?: IUser | null, character?: ICharacter | null}> =>
    team ? ((s == 'a' ? g.teamPicksA : g.teamPicksB) || []).map((p) => ({user: p.user as IUser, character: p.character as ICharacter}))
      : [{user: null, character: (s == 'a' ? g.pickA : g.pickB) || null}].filter((p) => !!p.character);
  const openGame = (g: {game?: string | null, duel?: string | null, mode?: string | null}) => {
    if(g.duel)
      history.push(`/duel/${g.duel}`);
    else if(g.game && g.mode && MODE_PATHS[g.mode])
      history.push(`/game/${MODE_PATHS[g.mode]}/${g.game}`);
  };
  const canOpen = (g: {game?: string | null, duel?: string | null, mode?: string | null}) => !!g.duel || (!!g.game && !!g.mode && !!MODE_PATHS[g.mode]);

  const timeline = (log: IDraftLogView[]) => (
    <div className={classes.timeline}>
      {log.map((e, i) => {
        const isBan = e.action == 'Ban';
        return (
          <div key={i} className={classes.step}>
            <span className={classes.stepNo}>{i + 1}</span>
            <span className={classes.stepAction} style={{color: isBan ? BAN : SIDE_COLORS[e.side]}}>{isBan ? 'Ban' : 'Pick'} · {e.side == 'a' ? 'A' : 'B'}</span>
            {e.character ? <img className={isBan ? classes.banIcon : classes.pickIcon} style={{width: 24, height: 24}} src={icon(e.character)} alt="" /> : <Block style={{fontSize: 18, color: PAGE_KIT.faint}} />}
            <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {e.character ? <b>{e.character.name}</b> : <span className={classes.muted}>{isBan ? 'No ban (out of time)' : 'No pick'}</span>}
              {e.user ? <span className={classes.muted}> · {isBan ? 'by ' : ''}{e.user.displayname}</span> : null}
              <span className={classes.muted}> · {sideName(e.side)}</span>
            </span>
            {e.auto ? <Tooltip title="The timer ran out, so the server made it"><span className={classes.tag} style={{color: '#ffcc80', background: 'rgba(255, 167, 38, 0.14)'}}><Timer style={{fontSize: 11, verticalAlign: -2}} /> Timer</span></Tooltip> : <span />}
          </div>
        );
      })}
    </div>
  );

  const gameCard = (g: {index: number, map?: number | null, mode?: string | null, winner?: 'a' | 'b' | null, forfeit?: boolean, game?: string | null, duel?: string | null,
    log?: IDraftLogView[], bansA?: ICharacter[], bansB?: ICharacter[], teamPicksA?: ITeamPickView[], teamPicksB?: ITeamPickView[], pickA?: ICharacter | null, pickB?: ICharacter | null}, live: boolean) => {
    const where = [team && g.mode, team && mapName(g.map)].filter(Boolean).join(' on ');
    const bans = {a: g.bansA || [], b: g.bansB || []};
    return (
      <div key={g.index} className={clsx(classes.game, {[classes.gameLive]: live})}>
        <div className={classes.gameHead}>
          <div style={{minWidth: 0}}>
            <div className={classes.gameTitle}>
              Game {g.index}
              {live && <span className={classes.tag} style={{marginLeft: 8, color: '#ef9a9a', background: 'rgba(239, 83, 80, 0.14)'}}>{PHASES[current?.phase || ''] || 'Live'}</span>}
              {g.forfeit && <span className={classes.tag} style={{marginLeft: 8, color: '#b0bec5', background: 'rgba(176, 190, 197, 0.14)'}}>Forfeit</span>}
            </div>
            <div className={classes.gameMeta}>
              {where || (team ? 'Map and mode to be chosen' : 'Ranked duel')}
              {g.winner ? <span> · <b style={{color: TOURNAMENT_ACCENT}}>{sideName(g.winner)}</b> won</span> : null}
            </div>
          </div>
          {canOpen(g) &&
            <Button variant="outlined" className={classes.openGame} onClick={() => openGame(g)}>
              {live ? <SportsEsports style={{fontSize: 16, marginRight: 6}} /> : <OpenInNew style={{fontSize: 15, marginRight: 6}} />}{live ? 'View the game' : team ? 'Open game' : 'Open duel'}
            </Button>}
        </div>
        <div className={classes.gameBody}>
          <div className={classes.gameSides}>
            {(['a', 'b'] as Array<'a' | 'b'>).map((s) => {
              const picks = picksOf(g, s);
              return (
                <div key={s}>
                  <div className={classes.label} style={{color: SIDE_COLORS[s], marginBottom: 4}}>{sideName(s)}</div>
                  {picks.length == 0 && <div className={classes.muted}>{live ? 'Not picked yet' : 'No picks recorded'}</div>}
                  {picks.map((p, i) => (
                    <div key={i} className={classes.pickRow}>
                      <img className={classes.pickIcon} src={icon(p.character)} alt="" />
                      <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        <b>{p.character?.name}</b>{p.user ? <span className={classes.muted}> · {p.user.displayname}</span> : null}
                      </span>
                    </div>
                  ))}
                  {bans[s].length > 0 &&
                    <div style={{marginTop: 8}}>
                      <span className={classes.label} style={{color: BAN}}>Banned · </span>
                      <span className={classes.icons} style={{verticalAlign: 'middle'}}>
                        {bans[s].map((c) => <Tooltip key={idOf(c)} title={`${c.name} · ${c.class} (banned)`}><img className={classes.banIcon} src={icon(c)} alt="" /></Tooltip>)}
                      </span>
                    </div>}
                </div>
              );
            })}
          </div>
          {(g.log || []).length > 0
            ? <div className={classes.section} style={{marginTop: 16}}><div className={classes.label}>Draft order</div>{timeline(g.log || [])}</div>
            : !live && <div className={classes.muted} style={{marginTop: 12}}>The draft order wasn't recorded for this game (it was played before the order was kept).</div>}
        </div>
      </div>
    );
  };

  const gamesTab = (
    <React.Fragment>
      {gameCount == 0 && <div className={classes.muted}>{m.status == MatchStatus.Completed ? 'Decided without games being played.' : 'No games yet: they appear here as soon as the first one is set up.'}</div>}
      {/* in the order they were played, the one being played last */}
      {games.slice().sort((x, y) => x.index - y.index).map((g: IGameView) => gameCard(g, false))}
      {current && !games.some((g) => g.index == current.index) &&
        gameCard({index: current.index, map: current.map, mode: current.mode, game: current.game, duel: current.duel, log: current.log,
          bansA: current.bansA, bansB: current.bansB, teamPicksA: current.teamPicksA, teamPicksB: current.teamPicksB, pickA: current.pickA, pickB: current.pickB}, true)}
    </React.Fragment>
  );

  return (
    <Dialog open={m != null} onClose={onClose} maxWidth={false} classes={{paper: classes.paper}}>
      <div className={classes.shell}>
        <div className={classes.head}>
          <IconButton className={classes.close} onClick={onClose}><Close /></IconButton>
          <div className={classes.kicker}>
            <span>{title}</span>
            <span className={classes.status} style={{color: status.color, background: `${status.color}22`}}>{status.label}</span>
            {m.manual && <Tooltip title="An organizer set this result by hand"><span className={classes.status} style={{color: '#b0bec5', background: 'rgba(176, 190, 197, 0.14)'}}>Set by hand</span></Tooltip>}
          </div>
          <div className={classes.versus}>
            <div style={{minWidth: 0}}>
              <div className={clsx(classes.sideName, {[classes.winnerName]: m.winner == 'a'})}>{sideName('a')}</div>
              <div className={classes.sideSub}>{m.a.seed ? `Seed ${m.a.seed}` : ''}</div>
            </div>
            <div>
              <div className={classes.score}>{m.a.score || 0}<span style={{color: PAGE_KIT.faint, margin: '0 10px'}}>–</span>{m.b.score || 0}</div>
              <div className={classes.scoreSub}>
                {m.status == MatchStatus.Ready && !m.readySince && m.startsAt ? <span><Schedule style={{fontSize: 12, verticalAlign: -2, marginRight: 3}} />{formatDateTime(m.startsAt)}</span> : `Bo${m.bestOf}`}
              </div>
            </div>
            <div style={{minWidth: 0, textAlign: 'right'}}>
              <div className={clsx(classes.sideName, {[classes.winnerName]: m.winner == 'b'})}>{sideName('b')}</div>
              <div className={classes.sideSub}>{m.b.seed ? `Seed ${m.b.seed}` : ''}</div>
            </div>
          </div>
        </div>
        <div className={classes.tabs}>
          <div role="button" className={clsx(classes.tab, {[classes.tabOn]: tab == 'overview'})} onClick={() => setTab('overview')}>Overview</div>
          <div role="button" className={clsx(classes.tab, {[classes.tabOn]: tab == 'games'})} onClick={() => setTab('games')}>Games{gameCount ? ` · ${gameCount}` : ''}</div>
          {manage && <div role="button" className={clsx(classes.tab, {[classes.tabOn]: tab == 'manage'})} onClick={() => setTab('manage')}>Manage</div>}
        </div>
        <div className={classes.body}>
          {tab == 'overview' && overview}
          {tab == 'games' && gamesTab}
          {tab == 'manage' && manage}
        </div>
      </div>
    </Dialog>
  );
}
